import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  LineChart,
  Line,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
  Cell,
} from 'recharts';
import {
  Activity,
  AlertCircle,
  Award,
  BarChart3,
  Camera,
  CheckCircle2,
  Eye,
  Gauge,
  HeartPulse,
  Layers,
  RefreshCw,
  ShieldCheck,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import PageHeader from '@/components/dashboard/PageHeader';
import { PQWordmark } from '@/components/pq/PQMark';

// The page title IS the brand mark (John, 2026-09-26). Rendered inside
// PageHeader's h1; alt text keeps it readable to screen readers.
const PQI_TITLE = (
  <PQWordmark height={56} className="my-1 max-w-full" alt="PQ Intelligence" />
);
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  loadPqiAnalytics,
  PQI_RANGES,
  formatPercent,
  formatUsd,
  titleizeJobType,
  titleizeEvent,
} from '@/lib/pqiAnalyticsData';
import PQVisionSection from '@/components/pqv/PQVisionSection';

const MIN_EVENTS = 10;

/* -------------------------------------------------------------------------
 * Section switcher — Analytics ↔ Vision. Lives inside the PageHeader's
 * actions slot. PQV Vision was added as a sub-section per spec; we do NOT
 * expose it as a top-level sidebar item.
 * ----------------------------------------------------------------------- */
const SECTION_TABS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'vision', label: 'Vision', icon: Camera },
];

const SectionSwitcher = ({ value, onValueChange }) => (
  <Tabs value={value} onValueChange={onValueChange}>
    <TabsList
      className="bg-[hsl(var(--card))] border border-border"
      data-testid="pqi-section-tabs"
    >
      {SECTION_TABS.map((s) => {
        const Icon = s.icon;
        return (
          <TabsTrigger
            key={s.id}
            value={s.id}
            className="data-[state=active]:bg-[hsl(var(--muted))] data-[state=active]:text-[hsl(var(--primary))] uppercase tracking-[0.16em] text-[11px]"
            data-testid={`pqi-section-${s.id}`}
          >
            <Icon size={12} className="mr-1.5" />
            {s.label}
          </TabsTrigger>
        );
      })}
    </TabsList>
  </Tabs>
);

/* -------------------------------------------------------------------------
 * Theme tokens used directly so charts pick up dark + gold + semantic colors.
 * ----------------------------------------------------------------------- */
const COLORS = {
  accent: 'hsl(45 38% 66%)', // gold
  accentSoft: 'hsl(45 38% 66% / 0.18)',
  healthy: 'hsl(150 56% 50%)',
  watch: 'hsl(38 92% 60%)',
  atRisk: 'hsl(354 70% 60%)',
  grid: 'hsl(0 0% 18%)',
  axis: 'hsl(0 0% 60%)',
  fg: 'hsl(0 0% 92%)',
  muted: 'hsl(0 0% 70%)',
};

const JOB_TYPE_COLORS = [
  'hsl(45 38% 66%)',  // gold
  'hsl(150 56% 50%)', // green
  'hsl(192 60% 60%)', // cyan
  'hsl(265 50% 65%)', // violet
  'hsl(38 92% 60%)',  // amber
  'hsl(354 70% 60%)', // rose
  'hsl(210 60% 65%)', // sky
  'hsl(120 40% 55%)', // emerald
];

const marginBucketColor = (m) => {
  const n = Number(m) || 0;
  if (n >= 25) return COLORS.healthy;
  if (n >= 10) return COLORS.watch;
  return COLORS.atRisk;
};

/* ------------------------------------------------------------------------
 * Reusable chart shell
 * ---------------------------------------------------------------------- */
const ChartCard = ({ title, subtitle, icon: Icon, children, className, testid }) => (
  <Card
    className={cn('border-[hsl(var(--hairline))] bg-[hsl(var(--card))]', className)}
    data-testid={testid}
  >
    <CardContent className="p-5 space-y-4">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-md bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
          {Icon ? <Icon className="h-4 w-4 text-[hsl(var(--accent))]" /> : null}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {subtitle || 'PQ Intelligence'}
          </div>
          <div className="text-base font-medium text-foreground truncate">{title}</div>
        </div>
      </div>
      <div className="w-full">{children}</div>
    </CardContent>
  </Card>
);

const KpiTile = ({ label, value, sub, icon: Icon, accent = 'gold', testid }) => {
  const accentClass =
    accent === 'green'
      ? 'text-[hsl(150_56%_50%)]'
      : accent === 'amber'
      ? 'text-[hsl(38_92%_60%)]'
      : accent === 'rose'
      ? 'text-[hsl(354_70%_60%)]'
      : 'text-[hsl(var(--accent))]';
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid={testid}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            {label}
          </span>
          {Icon ? <Icon className={cn('h-4 w-4', accentClass)} /> : null}
        </div>
        <div className="mt-1.5 text-2xl font-medium tracking-tight text-foreground">
          {value}
        </div>
        {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
      </CardContent>
    </Card>
  );
};

/* ------------------------------------------------------------------------
 * Custom tooltip that respects the dark theme
 * ---------------------------------------------------------------------- */
const DarkTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-border bg-[hsl(var(--card))]/95 backdrop-blur px-3 py-2 shadow-lg text-xs">
      {label != null ? (
        <div className="text-muted-foreground mb-1">{String(label)}</div>
      ) : null}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            aria-hidden
            className="h-2 w-2 rounded-full"
            style={{ background: p.color || p.payload?.fill || COLORS.accent }}
          />
          <span className="text-foreground">
            {p.name}: {formatter ? formatter(p.value, p) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const axisProps = {
  stroke: COLORS.axis,
  tickLine: false,
  axisLine: { stroke: COLORS.grid },
  style: { fontSize: 11 },
};

/* ========================================================================
 * Main page
 * ===================================================================== */
export default function PQIIntelligencePage() {
  const { t } = useTranslation();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // PQ Vision lives inside this page as a second section. Default to the
  // existing Analytics view so the long-standing route behavior is preserved.
  const [section, setSection] = useState('analytics');

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const days = PQI_RANGES.find((r) => r.id === range)?.days ?? null;
      const result = await loadPqiAnalytics({ days });
      setData(result);
    } catch (e) {
      setError(e?.message || 'Failed to load PQ Intelligence.');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Compute eventTypeKeys here, BEFORE any early returns, to keep hook order stable.
  const eventTypeKeys = useMemo(() => {
    const found = new Set();
    for (const row of data?.telemetryVolume || []) {
      Object.keys(row).forEach((k) => {
        if (k !== 'day' && k !== 'total') found.add(k);
      });
    }
    return Array.from(found).sort();
  }, [data]);

  /* ---------------- Render ---------------- */
  // Vision section — independent of analytics data load.
  if (section === 'vision') {
    return (
      <div className="space-y-6" data-testid="pqi-page-vision">
        <PageHeader
          eyebrow="PQ Vision"
          title={PQI_TITLE}
          subtitle="Walkthrough captures — review areas, photos, and notes for any job you've documented."
          actions={
            <SectionSwitcher value={section} onValueChange={setSection} />
          }
        />
        <PQVisionSection />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-5" data-testid="pqi-page-loading">
        <PageHeader
          eyebrow="Analytics"
          title={PQI_TITLE}
          subtitle="Silent telemetry collected across estimates, jobs, costs, and outcomes."
          actions={
            <SectionSwitcher value={section} onValueChange={setSection} />
          }
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 bg-[hsl(var(--muted))]" />
          ))}
        </div>
        <Skeleton className="h-72 bg-[hsl(var(--muted))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5" data-testid="pqi-page-error">
        <PageHeader
          eyebrow="Analytics"
          title={PQI_TITLE}
          actions={
            <SectionSwitcher value={section} onValueChange={setSection} />
          }
        />
        <Card className="border-rose-500/30 bg-rose-500/5">
          <CardContent className="p-5 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-foreground">{error}</div>
              <Button
                onClick={reload}
                variant="outline"
                className="mt-3 border-border"
                data-testid="pqi-retry"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalEvents = data?.totalCount ?? 0;
  const insufficient = totalEvents < MIN_EVENTS;
  const {
    health,
    marginTrend,
    jobTypePerf,
    phaseDuration,
    costVariance,
    telemetryVolume,
    winRate,
  } = data;

  return (
    <div className="space-y-6" data-testid="pqi-page">
      <PageHeader
        eyebrow="Analytics"
        title={PQI_TITLE}
        subtitle={t('pqi.subtitle', {
          defaultValue:
            "Your margins, won rate, what's working and where there's room to improve.",
        })}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <SectionSwitcher value={section} onValueChange={setSection} />
            <Tabs value={range} onValueChange={setRange}>
              <TabsList className="bg-[hsl(var(--card))] border border-border">
                {PQI_RANGES.map((r) => (
                  <TabsTrigger
                    key={r.id}
                    value={r.id}
                    data-testid={`pqi-range-${r.id}`}
                  >
                    {r.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <Button
              onClick={reload}
              variant="outline"
              className="border-border"
              data-testid="pqi-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        }
      />

      {/* Margin Health Tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Avg Margin"
          value={formatPercent(health.avgMargin)}
          sub={`${health.totalEvents} events tracked`}
          icon={Gauge}
          accent={
            health.avgMargin >= 25
              ? 'green'
              : health.avgMargin >= 10
              ? 'amber'
              : 'rose'
          }
          testid="pqi-tile-avg-margin"
        />
        <KpiTile
          label="Median Margin"
          value={formatPercent(health.medianMargin)}
          sub={`${health.healthy + health.watch + health.atRisk} jobs scored`}
          icon={HeartPulse}
          accent={
            health.medianMargin >= 25
              ? 'green'
              : health.medianMargin >= 10
              ? 'amber'
              : 'rose'
          }
          testid="pqi-tile-median-margin"
        />
        <KpiTile
          label="Won Rate"
          value={formatPercent(health.signedRate, 0)}
          sub={`${health.signedCount} won · ${Math.max(
            0,
            health.estimatedCount - health.signedCount
          )} learned`}
          icon={Award}
          accent="gold"
          testid="pqi-tile-win-rate"
        />
        <KpiTile
          label={t('pqi.kpi.job_status', { defaultValue: 'Job Status' })}
          value={`${health.healthy} / ${health.watch} / ${health.atRisk}`}
          sub="Healthy · Watch · At-risk"
          icon={ShieldCheck}
          accent="gold"
          testid="pqi-tile-job-posture"
        />
      </div>

      {/* Insufficient data — friendly empty state, but still render KPI tiles above */}
      {insufficient ? (
        <Card
          className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
          data-testid="pqi-empty-state"
        >
          <CardContent className="p-10 text-center">
            <Activity className="h-10 w-10 mx-auto text-[hsl(var(--accent))] mb-3" />
            <div className="text-base text-foreground mb-1">
              Gathering data
            </div>
            <div className="text-sm text-muted-foreground max-w-md mx-auto">
              You have <span className="text-foreground font-medium">{totalEvents}</span> tracked event
              {totalEvents === 1 ? '' : 's'} so far. Keep working — your insights start
              appearing once you cross 10 events. Every estimate, invoice, phase, and material log adds signal.
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Margin Trend */}
          <ChartCard
            subtitle="Trend"
            title="Margin Over Time"
            icon={TrendingUp}
            testid="pqi-chart-margin-trend"
          >
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={marginTrend}>
                  <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="tsLabel" {...axisProps} />
                  <YAxis
                    {...axisProps}
                    domain={['auto', 'auto']}
                    tickFormatter={(v) => `${v.toFixed(0)}%`}
                  />
                  <Tooltip
                    content={<DarkTooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />}
                    cursor={{ stroke: COLORS.accent, strokeOpacity: 0.2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="marginPct"
                    name="Margin %"
                    stroke={COLORS.accent}
                    strokeWidth={2}
                    dot={{ r: 3, fill: COLORS.accent, stroke: COLORS.accent }}
                    activeDot={{ r: 5 }}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          {/* Two-column row: Job Type Performance + Win Rate */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              subtitle="Performance"
              title="Avg Margin by Job Type"
              icon={Layers}
              testid="pqi-chart-job-type"
            >
              <div className="h-60">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={jobTypePerf} layout="vertical" margin={{ left: 32 }}>
                    <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      {...axisProps}
                      tickFormatter={(v) => `${v.toFixed(0)}%`}
                    />
                    <YAxis
                      type="category"
                      dataKey="jobType"
                      tickFormatter={titleizeJobType}
                      {...axisProps}
                      width={92}
                    />
                    <Tooltip
                      content={<DarkTooltip formatter={(v) => `${Number(v).toFixed(1)}%`} />}
                      cursor={{ fill: COLORS.accentSoft }}
                    />
                    <Bar dataKey="avgMargin" name="Avg Margin %" radius={[0, 4, 4, 0]}>
                      {jobTypePerf.map((row, idx) => (
                        <Cell key={idx} fill={JOB_TYPE_COLORS[idx % JOB_TYPE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartCard>

            <ChartCard
              subtitle={t('pqi.section.funnel', {
                defaultValue: 'LEAD → SIGNED',
              })}
              title="Estimate → Sign Rate"
              icon={Target}
              testid="pqi-chart-win-rate"
            >
              <div className="h-60">
                {winRate.recentSeries.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No estimates created yet in this window.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={winRate.recentSeries}>
                      <defs>
                        <linearGradient id="winGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={COLORS.accent} stopOpacity={0.4} />
                          <stop offset="100%" stopColor={COLORS.accent} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="week" {...axisProps} />
                      <YAxis
                        {...axisProps}
                        domain={[0, 100]}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        content={<DarkTooltip formatter={(v, p) => `${Number(v).toFixed(0)}%`} />}
                        cursor={{ stroke: COLORS.accent, strokeOpacity: 0.2 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="rate"
                        name="Signed %"
                        stroke={COLORS.accent}
                        strokeWidth={2}
                        fill="url(#winGrad)"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ChartCard>
          </div>

          {/* Phase Duration + Cost Variance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ChartCard
              subtitle="Operational"
              title="Avg Days Between Phases"
              icon={Zap}
              testid="pqi-chart-phase-duration"
            >
              <div className="h-60">
                {phaseDuration.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    Not enough phase advances yet.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={phaseDuration}>
                      <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="phase" {...axisProps} />
                      <YAxis
                        {...axisProps}
                        tickFormatter={(v) => `${v.toFixed(1)}d`}
                      />
                      <Tooltip
                        content={<DarkTooltip formatter={(v) => `${Number(v).toFixed(1)} days`} />}
                        cursor={{ fill: COLORS.accentSoft }}
                      />
                      <Bar dataKey="avgDays" name="Avg days" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ChartCard>

            <ChartCard
              subtitle="Estimating Accuracy"
              title="Bid vs Actual Cost"
              icon={BarChart3}
              testid="pqi-chart-cost-variance"
            >
              <div className="h-60">
                {costVariance.length === 0 ? (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No closed jobs yet — close a job to see variance.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart>
                      <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        dataKey="bid"
                        name="Bid"
                        {...axisProps}
                        tickFormatter={formatUsd}
                      />
                      <YAxis
                        type="number"
                        dataKey="actual"
                        name="Actual"
                        {...axisProps}
                        tickFormatter={formatUsd}
                      />
                      <ZAxis range={[60, 200]} />
                      <Tooltip
                        content={
                          <DarkTooltip
                            formatter={(v, p) =>
                              p?.dataKey === 'bid' || p?.dataKey === 'actual'
                                ? formatUsd(v)
                                : v
                            }
                          />
                        }
                        cursor={{ stroke: COLORS.accent, strokeOpacity: 0.2 }}
                      />
                      <Scatter data={costVariance} name="Closed Jobs">
                        {costVariance.map((p, i) => (
                          <Cell key={i} fill={marginBucketColor(p.marginPct)} />
                        ))}
                      </Scatter>
                    </ScatterChart>
                  </ResponsiveContainer>
                )}
              </div>
            </ChartCard>
          </div>

          {/* Telemetry Volume — events/day stacked area */}
          <ChartCard
            subtitle={t('pqi.section.activity', {
              defaultValue: 'ACTIVITY',
            })}
            title={t('pqi.chart.info_collected', {
              defaultValue: 'Info Collected',
            })}
            icon={Eye}
            testid="pqi-chart-telemetry"
          >
            <div className="h-56">
              {telemetryVolume.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  No telemetry in this window.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={telemetryVolume}>
                    <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" {...axisProps} />
                    <YAxis {...axisProps} allowDecimals={false} />
                    <Tooltip content={<DarkTooltip />} cursor={{ stroke: COLORS.accent, strokeOpacity: 0.2 }} />
                    {eventTypeKeys.map((k, idx) => (
                      <Area
                        key={k}
                        type="monotone"
                        dataKey={k}
                        name={titleizeEvent(k)}
                        stackId="evt"
                        stroke={JOB_TYPE_COLORS[idx % JOB_TYPE_COLORS.length]}
                        fill={JOB_TYPE_COLORS[idx % JOB_TYPE_COLORS.length]}
                        fillOpacity={0.35}
                        isAnimationActive={false}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </ChartCard>

          {/* Footer — reassurance copy. Main line stays in the existing
              uppercase eyebrow treatment so we don't break the visual rhythm
              of the page; the subtext is a smaller, muted, italic aside that
              reads as an intentional, knowing wink. */}
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
              {t('pqi.footer_main', {
                defaultValue:
                  'Tracked automatically as you work. Your data only — locked to your account.',
              })}
            </div>
            <div className="text-[10px] italic text-muted-foreground/60 pl-[22px]">
              {t('pqi.footer_aside', {
                defaultValue: "(translation: your shit's safe)",
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
